import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CoachMode } from "@shared/constants";
import { api } from "@/lib/api";
import type { Conversation, ConversationSummary } from "@/lib/types";

export function useConversations(opts: { q: string; archived: boolean }) {
  return useQuery({
    queryKey: ["conversations", "list", opts],
    queryFn: async () =>
      (await api<{ data: ConversationSummary[] }>(`/api/conversations?archived=${opts.archived}${opts.q ? `&q=${encodeURIComponent(opts.q)}` : ""}`)).data,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: ["conversations", "detail", id],
    queryFn: async () => (await api<{ data: Conversation }>(`/api/conversations/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useConversationMutations() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["conversations"] });
  return {
    create: useMutation({
      mutationFn: async (mode: CoachMode) => (await api<{ data: ConversationSummary }>("/api/conversations", { method: "POST", json: { mode } })).data,
      onSuccess: refresh,
      onError: (e) => toast.error(e.message),
    }),
    update: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; title?: string; mode?: CoachMode; archived?: boolean }) =>
        api(`/api/conversations/${id}`, { method: "PATCH", json: patch }),
      onSuccess: refresh,
      onError: (e) => toast.error(e.message),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/conversations/${id}`, { method: "DELETE" }),
      onSuccess: refresh,
      onError: (e) => toast.error(e.message),
    }),
  };
}
